import React, { useEffect, useState } from 'react';
import type { FC } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { IconButton } from '@atlaskit/button/new';
import TasksIcon from '@atlaskit/icon/core/tasks';
import { Flex, Pressable } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { CreatePageButton } from '@confluence/contextual-create';
import { useSSRPlaceholderReplaceIdProp, LoadableAfterPaint } from '@confluence/loadable';
import { ContextualCreateContentPopoverLoader } from '@confluence/create-blank-fabric-page/entry-points/ContextualCreateContentPopover';
import { useIsCreationDropdownEnabled } from '@confluence/content-types-utils';
import { useIsNav4Enabled } from '@confluence/nav4-enabled';
import { useContentTreeSelection } from '@confluence/content-tree-actions';
import { fg } from '@confluence/feature-gating';
import { useExpandSideNav } from '@atlassian/navigation-system/layout/side-nav';

import type { SetSelection } from './SpaceViewsMenu';
import { useTransitionId } from '@confluence/route-manager';

const SpaceViewsMenuLoader = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SpaceViewsMenu" */ './SpaceViewsMenu'))
			.SpaceViewsMenu,
});

type PagesItemActionsProps = {
	isLicensed: boolean;
	edition?: string;
	selection: string;
	setSelection: SetSelection;
	spaceKey: string;
	expandContentSection?: () => void;
	onItemClick?: () => void;
};

export const PagesItemActions: FC<PagesItemActionsProps> = ({
	isLicensed,
	edition,
	selection,
	setSelection,
	spaceKey,
	expandContentSection,
	onItemClick,
}) => {
	const intl = useIntl();
	const canShowFolder = selection === 'TREE';
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const isCreationADropdown = useIsCreationDropdownEnabled();
	const [isContextualCreateFocused, setIsContextualCreateFocused] = useState<boolean>(false);
	const [isContextualCreatePopupOpen, setIsContextualCreatePopupOpen] = useState<boolean>(false);
	const isNav4Enabled = useIsNav4Enabled();
	const [{ isMultiSelectActive }, { toggleMultiSelect, clearSelection }] =
		useContentTreeSelection();

	const expandSideNav = useExpandSideNav();
	const transitionId = useTransitionId();
	useEffect(() => {
		if (transitionId && transitionId > 0) {
			toggleMultiSelect(false);
			clearSelection();
		}
	}, [transitionId, toggleMultiSelect, clearSelection]);

	const spaceViewsMenu = isLicensed ? (
		<SpaceViewsMenuLoader
			selection={selection}
			setSelection={setSelection}
			isWhiteboardBetaEnabled={isCreationADropdown}
			edition={edition}
		/>
	) : null;

	const contextualCreate = (
		<>
			{isCreationADropdown ? (
				<ContextualCreateContentPopoverLoader
					isContextualCreateFocused={isContextualCreateFocused}
					setIsContextualCreateFocused={setIsContextualCreateFocused}
					shouldShowPopup={isContextualCreatePopupOpen}
					setShouldShowPopup={setIsContextualCreatePopupOpen}
					spaceKey={spaceKey}
					source="spaceLevelContextualCreate"
					rootCreateButtonStyling
					onItemClick={onItemClick}
					shouldRenderToParent
					hideFolder={!canShowFolder}
				/>
			) : (
				<CreatePageButton spaceKey={spaceKey} />
			)}
		</>
	);

	const multiSelectButton = (
		<IconButton
			icon={TasksIcon}
			label={intl.formatMessage(i18n.enableMultiSelect)}
			appearance="subtle"
			spacing="compact"
			onClick={() => {
				expandSideNav();
				expandContentSection?.();
				toggleMultiSelect(true);
			}}
			isTooltipDisabled={false}
		/>
	);

	const cancelMultiSelectButton = (
		<Pressable
			aria-label="Cancel multi-select mode"
			onClick={() => toggleMultiSelect(false)}
			xcss={cx(styles.cancel)}
			paddingInline="space.050"
		>
			{intl.formatMessage(i18n.cancelMultiSelect)}
		</Pressable>
	);

	const nav4Actions = isMultiSelectActive ? (
		<>{cancelMultiSelectButton}</>
	) : (
		<>
			{edition === 'PREMIUM' && fg('confluence_page_tree_with_actions') && multiSelectButton}
			{contextualCreate}
			{!(edition === 'PREMIUM' && fg('confluence_page_tree_with_actions')) && spaceViewsMenu}
		</>
	);

	return (
		<Flex data-vc="pages-item-actions" {...ssrPlaceholderIdProp}>
			{isNav4Enabled ? (
				<>{nav4Actions}</>
			) : (
				<>
					{spaceViewsMenu}
					{contextualCreate}
				</>
			)}
		</Flex>
	);
};

const i18n = defineMessages({
	cancelMultiSelect: {
		id: 'space-views.cancel-multi-select',
		defaultMessage: 'Cancel',
		description: 'Text to cancel the multi-select mode and exit bulk actions in the content tree',
	},
	enableMultiSelect: {
		id: 'space-views.enable-multi-select',
		defaultMessage: 'Select to edit multiple items',
		description: 'Text to enable multi-select mode in the content tree',
	},
});

const styles = cssMap({
	cancel: {
		zIndex: 1,
		height: '20px',
		font: token('font.body'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.selected'),
		'&:hover': {
			textDecoration: 'underline',
		},
	},
});
